/**
 * #154 — the shared-binary-book cue and the Vol/Liq column header tooltips.
 *
 * A Polymarket binary market has ONE book. The UI projects it into two rendered
 * outcomes (away/home on a moneyline, Over/Under on a spread or total line), and
 * `marketStore` deliberately writes the same market-wide `volume` and `liquidity`
 * onto both sides. The issue's acceptance criterion is that such an aggregate is
 * "visibly identified as shared, or shown once, so complementary outcome rows do
 * not look like independent totals" — this module holds the wording for that cue;
 * the marker itself is a superscript "ˢ" rendered next to the value.
 *
 * The strings live here rather than at each call site because the same sentences
 * are rendered by GameTable, MultiOutcomeTable, SpreadsTotalsTable and the three
 * GameDetail sections — eight copies of one string drifted apart before.
 */

/**
 * Tooltips for the shared-book marker.
 *
 * Deliberately says "both outcomes of one binary market" and NOT "shown on both
 * teams' rows": the aggregate is duplicated across two team rows only on the
 * moneyline pair. On the spread/total surfaces the same flagged book is rendered
 * ONCE per row, and an Over/Under line has no "teams" at all. The claim that
 * holds on every surface is the one about the BOOK, not about how many rows
 * happen to show it — and getting that claim right is the whole point of #154.
 */
export const SHARED_VOL_TOOLTIP =
  'Shared market-wide traded volume — one binary market underlies both outcomes, so this is the whole book’s total, not an independent per-outcome total';

export const SHARED_LIQ_TOOLTIP =
  'Shared market-wide book liquidity — one binary market underlies both outcomes, so this is the whole book’s total, not an independent per-outcome total';

/**
 * "Vol" column header tooltips. Two variants, because the shared-book marker is a
 * POLYMARKET-only cue: `isSharedBinaryBook` is set solely by the poly moneyline
 * pair and the poly spread/total lines, so a Kalshi header that advertised the
 * marker would be documenting something that cannot appear in its own column.
 * The rest of the sentence is identical, and both are shared by every table that
 * renders these columns so the wording can't drift copy to copy.
 */
export const TOTAL_VOLUME_LABEL = 'Total Vol';
export const KALSHI_TOTAL_VOLUME_LABEL = 'K Total Vol';
export const POLY_TOTAL_VOLUME_LABEL = 'P Total Vol';

const VOL_HEADER_LADDER_NOTE =
  'Expanded ladder rows: Resting Qty — contracts resting at each price, NOT traded. A leading ~ marks a resting-depth fallback';

export const VOL_HEADER_TOOLTIP_KALSHI =
  `Kalshi lifetime traded volume since the market opened, not rolling 24-hour volume. ${VOL_HEADER_LADDER_NOTE}.`;

export const VOL_HEADER_TOOLTIP_POLY =
  `Polymarket lifetime traded volume since the market opened, not rolling 24-hour volume. ${VOL_HEADER_LADDER_NOTE}; a superscript ˢ on the Total + Liq cells marks one binary book's market-wide total, not an independent per-outcome total.`;

/** "Liq" column header tooltip — same market-vs-ladder split as Vol. */
export const LIQ_HEADER_TOOLTIP_KALSHI =
  'Market rows: total $ resting in book (bids + asks). Expanded ladder rows: Resting $ — price × size resting at that price.';

export const LIQ_HEADER_TOOLTIP_POLY = `${LIQ_HEADER_TOOLTIP_KALSHI.slice(0, -1)}; a superscript ˢ marks one binary book's market-wide total, not an independent per-outcome total.`;

// ── Polymarket US (V55 / F2) — the middle venue column ─────────────────────
// Same single-binary-per-game shape as intl Polymarket, so the same superscript
// cue and the same lifetime-volume semantic apply; only the venue name differs.
export const POLY_US_TOTAL_VOLUME_LABEL = 'US Total Vol';

export const VOL_HEADER_TOOLTIP_POLY_US =
  `Polymarket US lifetime traded volume since the market opened, not rolling 24-hour volume. ${VOL_HEADER_LADDER_NOTE}; a superscript ˢ on the Total + Liq cells marks one binary book's market-wide total, not an independent per-outcome total.`;

export const LIQ_HEADER_TOOLTIP_POLY_US = LIQ_HEADER_TOOLTIP_POLY;
